// Who an error_log row belongs to, in words the Errors panel can print.
//
// A row has a user_id only when the report was written under a live session.
// Everything else carries only client_id — a random value the logger keeps in
// that browser's localStorage — so a signed-out visitor, a crawler, or a
// headless browser with fresh storage on every run is a "browser", never a
// "user". The panel used to count those ids as "5 users" and stamp the row
// "authenticated" because the RPC path is named that, whoever calls it.

export interface ErrorIdentityRow {
  user_id?: string | null;
  client_id?: string | null;
}

export interface PersonLookup {
  full_name?: string | null;
  email?: string | null;
}

/** One line naming who hit this error: the person, or how many signed-out browsers. */
export function describeWho(
  rows: ErrorIdentityRow[],
  lookup: (userId: string) => PersonLookup | undefined,
): string {
  const people = new Set(rows.map((r) => r.user_id).filter((id): id is string => !!id));
  const browsers = new Set(rows.filter((r) => !r.user_id).map((r) => r.client_id ?? 'anon'));
  const parts: string[] = [];
  if (people.size === 1) {
    const [id] = people;
    const p = lookup(id);
    parts.push(p?.full_name || p?.email || `user ${id.slice(0, 8)}`);
  } else if (people.size > 1) {
    parts.push(`${people.size} people`);
  }
  if (browsers.size > 0) {
    parts.push(`${browsers.size} signed-out browser${browsers.size === 1 ? '' : 's'}`);
  }
  return parts.join(' + ') || 'unknown';
}

/** Whether the report was written under a session. */
export function sessionLabel(row: ErrorIdentityRow): 'Signed in' | 'Signed out' {
  return row.user_id ? 'Signed in' : 'Signed out';
}

/** The delivery path, named for what it is rather than who the caller was. */
export function deliveryLabel(source: string | null | undefined): string {
  if (source === 'unauthenticated') return 'session-free edge function';
  if (source === 'authenticated') return 'log_error RPC';
  return source || 'unknown';
}

const BOT = /bot|crawl|spider|slurp|curl\/|wget\/|python|httpclient|headless|phantom|puppeteer|playwright|lighthouse|pingdom|uptime|monitor/i;

/** A short reading of a user-agent string: browser and platform, or the automation that sent it. */
export function describeUserAgent(ua: string | null | undefined): string {
  if (!ua) return 'unknown browser';
  const headless = /HeadlessChrome\/([\d.]+)/.exec(ua);
  if (headless) return `HeadlessChrome ${headless[1].split('.')[0]} (automation)`;
  if (BOT.test(ua)) return `${ua.slice(0, 60)} (automation)`;

  const platform = /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
    : /Android/.test(ua) ? 'Android'
    : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X|Macintosh/.test(ua) ? 'Mac'
    : /CrOS/.test(ua) ? 'ChromeOS'
    : /Linux/.test(ua) ? 'Linux'
    : null;

  const pick = (re: RegExp, name: string) => {
    const m = re.exec(ua);
    return m ? `${name} ${m[1].split('.')[0]}` : null;
  };
  const browser =
    pick(/Edg(?:e|A|iOS)?\/([\d.]+)/, 'Edge') ??
    pick(/OPR\/([\d.]+)/, 'Opera') ??
    pick(/SamsungBrowser\/([\d.]+)/, 'Samsung Internet') ??
    pick(/Firefox\/([\d.]+)/, 'Firefox') ??
    pick(/FxiOS\/([\d.]+)/, 'Firefox') ??
    pick(/CriOS\/([\d.]+)/, 'Chrome') ??
    pick(/Chrome\/([\d.]+)/, 'Chrome') ??
    (/Safari\//.test(ua) ? pick(/Version\/([\d.]+)/, 'Safari') ?? 'Safari' : null) ??
    'unknown browser';

  return platform ? `${browser} on ${platform}` : browser;
}
